import { Link, createFileRoute } from '@tanstack/react-router'
import { formatearAntiguedad } from '@/lib/fecha'
import { componerENCF } from '@/lib/encf'
import { Esqueleto, LineaEsqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { ENCF, FechaHora } from '@/ui/Numero'
import { useFallidos, useResumenConsola } from '@/dominio/consola/useConsola'
import type { TrabajoFallido } from '@/dominio/consola/tipos'
import { EncabezadoPantalla, Tarjeta } from '../-pantalla'
import { CifraConsola, ErrorDeCarga } from './-comun'

/**
 * Portada de la consola: el resumen de la plataforma y los fallidos recientes.
 *
 * Nada más. Lo que el dueño necesita al abrir es saber si algo está roto y
 * dónde; los gráficos de tendencia son exactamente lo que hace que un panel
 * parezca generado (`docs/07`, Tablero).
 */
export const Route = createFileRoute('/consola/')({
  component: PortadaConsola,
})

const RECIENTES = 10

function PortadaConsola() {
  const resumen = useResumenConsola()
  const fallidos = useFallidos(RECIENTES)
  const datos = resumen.data

  return (
    <div className="flex flex-col gap-6">
      <EncabezadoPantalla titulo="Portada" descripcion="El estado de la plataforma entera, de todos los clientes." />

      {resumen.isError ? (
        <ErrorDeCarga error={resumen.error} queCargaba="el resumen" onReintentar={() => void resumen.refetch()} />
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {resumen.isLoading ? (
            Array.from({ length: 8 }, (_, i) => <Esqueleto key={i} className="h-[92px]" />)
          ) : (
            <>
              <CifraConsola
                etiqueta="Clientes activos"
                valor={datos?.tenantsActivos}
                destino="/consola/clientes"
                filtros={{ estado: 'activo' }}
              />
              <CifraConsola
                etiqueta="En prueba"
                valor={datos?.tenantsPrueba}
                destino="/consola/clientes"
                filtros={{ estado: 'prueba' }}
              />
              <CifraConsola
                etiqueta="Suspendidos"
                valor={datos?.tenantsSuspendidos}
                tono="ambar"
                destino="/consola/clientes"
                filtros={{ estado: 'suspendido' }}
              />
              <CifraConsola
                etiqueta="Documentos hoy"
                valor={datos?.documentosHoy}
                nota="emitidos en todos los clientes"
              />
              <CifraConsola
                etiqueta="Trabajos fallidos"
                valor={datos?.trabajosFallidos}
                tono="rojo"
                nota="se agotaron los reintentos"
                destino="/consola/fallidos"
              />
              <CifraConsola
                etiqueta="Contingencias vigentes"
                valor={datos?.contingenciasVigentes}
                tono="ambar"
                nota="72 h para transmitir · 15 días de permanencia"
              />
              <CifraConsola
                etiqueta="Certificados por vencer"
                valor={datos?.certificadosPorVencer}
                tono="ambar"
                nota="en los próximos 30 días"
              />
              <CifraConsola
                etiqueta="Certificados vencidos"
                valor={datos?.certificadosVencidos}
                tono="rojo"
                nota="esos clientes no pueden firmar"
              />
            </>
          )}
        </div>
      )}

      <Tarjeta
        titulo="Fallidos recientes"
        relleno={false}
        acciones={
          <Link to="/consola/fallidos" className="text-etiqueta text-marca hover:underline">
            Ver la bandeja completa
          </Link>
        }
      >
        {fallidos.isLoading ? (
          <div className="flex flex-col gap-3 p-4">
            {Array.from({ length: 4 }, (_, i) => (
              <LineaEsqueleto key={i} ancho={i % 2 === 0 ? 'w-3/4' : 'w-1/2'} />
            ))}
          </div>
        ) : fallidos.isError ? (
          <ErrorDeCarga error={fallidos.error} queCargaba="los fallidos" onReintentar={() => void fallidos.refetch()} />
        ) : (fallidos.data ?? []).length === 0 ? (
          <EstadoVacio variante="resuelto" mensaje="Sin trabajos fallidos." />
        ) : (
          <ul className="flex flex-col">
            {(fallidos.data ?? []).map((trabajo) => (
              <FilaFallidoReciente key={trabajo.id} trabajo={trabajo} />
            ))}
          </ul>
        )}
      </Tarjeta>
    </div>
  )
}

function FilaFallidoReciente({ trabajo }: { trabajo: TrabajoFallido }) {
  const encf =
    trabajo.tipoEcf !== null && trabajo.tipoEcf !== undefined && trabajo.secuencia !== null && trabajo.secuencia !== undefined
      ? componerENCF(trabajo.tipoEcf, trabajo.secuencia)
      : null

  return (
    <li className="flex flex-wrap items-baseline gap-3 border-b border-gris-200 px-4 py-2 last:border-b-0">
      <Link
        to="/consola/clientes/$id"
        params={{ id: trabajo.tenantId }}
        search={{ pestana: 'colas' }}
        className="min-w-[200px] truncate text-cuerpo text-gris-900 hover:text-marca hover:underline"
        title={trabajo.razonSocial}
      >
        {trabajo.razonSocial}
      </Link>
      <span className="text-etiqueta text-gris-600">
        {trabajo.cola} · {trabajo.tipoTrabajo}
      </span>
      {encf !== null ? <ENCF valor={encf} /> : null}
      <span className="min-w-0 flex-1 truncate text-etiqueta text-gris-700" title={trabajo.ultimoError ?? undefined}>
        {trabajo.ultimoCodigoFallo ? <span className="mono mr-1">{trabajo.ultimoCodigoFallo}</span> : null}
        {trabajo.ultimoError ?? 'sin detalle del fallo'}
      </span>
      <span className="text-etiqueta text-gris-600">
        hace {formatearAntiguedad(trabajo.actualizadoEl)} · <FechaHora valor={trabajo.actualizadoEl} />
      </span>
    </li>
  )
}
